import {
  ANNOUNCEMENT_DEBOUNCE_MS,
  ANNOUNCEMENT_REPEAT_GAP_MS,
} from 'Services/announcementService/index.constants';
import type {
  AnnouncementPoliteness,
  AnnouncementTopic,
} from 'Services/announcementService/index.types';
import {
  EMPTY_ANNOUNCEMENTS,
  useAnnouncementStore,
} from 'Services/announcementService/store';
import { pushToGTMDataLayer } from 'Utils/analytics';

const POLITENESS_CHANNELS: AnnouncementPoliteness[] = ['polite', 'assertive'];

/** One queued message, waiting out its debounce. */
interface PendingAnnouncement {
  message: string;
  event?: string;
  topic?: AnnouncementTopic;
}

/**
 * Decides *when* the app's live regions change: callers queue a message, this
 * debounces it, and the text lands in `useAnnouncementStore`.
 *
 * @remarks
 * The polite and assertive channels are independent — an urgent announcement
 * neither delays nor cancels one already in flight.
 *
 * Within a channel, `topic` decides whether a new message *corrects* the one
 * waiting or *follows* it.
 *
 * The timers live here rather than in the store because `setTimeout` handles
 * are not state: they are not serializable and have no meaning to a subscriber.
 */
export class AnnouncementService {
  private static instance: AnnouncementService | null = null;

  private readonly pending: Partial<
    Record<AnnouncementPoliteness, PendingAnnouncement>
  > = {};

  private readonly timers: Partial<
    Record<AnnouncementPoliteness, ReturnType<typeof setTimeout>>
  > = {};

  /** Tests use `new AnnouncementService()` so pending timers cannot leak. */
  static getInstance(): AnnouncementService {
    if (!AnnouncementService.instance) {
      AnnouncementService.instance = new AnnouncementService();
    }

    return AnnouncementService.instance;
  }

  /**
   * Deferred, so a burst announces only the state that settled.
   *
   * `event` is reported only if this message survives the debounce and reaches
   * the region; a superseded or unchanged one is never counted.
   *
   * `topic` is what keeps a *sequence* from being collapsed like a *toggle*:
   * a message that changes topic writes the one still waiting before taking
   * its place, so neither step is lost. Same topic (or none on either side)
   * keeps the latest-wins behaviour a flipping state needs.
   */
  announce = (
    message: string,
    politeness: AnnouncementPoliteness = 'polite',
    event?: string,
    topic?: AnnouncementTopic
  ): void => {
    if (!message) {
      return;
    }

    const waiting = this.pending[politeness];

    if (waiting && waiting.topic !== topic) {
      this.flush(politeness);
    }

    this.pending[politeness] = { message, event, topic };
    this.clearTimer(politeness);
    this.timers[politeness] = setTimeout(
      () => this.flush(politeness),
      ANNOUNCEMENT_DEBOUNCE_MS
    );
  };

  /** Empties both regions and drops anything still pending. */
  reset = (): void => {
    POLITENESS_CHANNELS.forEach((politeness) => {
      this.clearTimer(politeness);
      delete this.pending[politeness];
    });

    useAnnouncementStore.setState({ ...EMPTY_ANNOUNCEMENTS });
  };

  private clearTimer(politeness: AnnouncementPoliteness): void {
    if (this.timers[politeness]) {
      clearTimeout(this.timers[politeness]);
      delete this.timers[politeness];
    }
  }

  private flush(politeness: AnnouncementPoliteness): void {
    const entry = this.pending[politeness];
    delete this.pending[politeness];
    delete this.timers[politeness];

    if (!entry) {
      return;
    }

    // Rewriting the words the region already holds is not a DOM change, so a
    // screen reader stays silent. Blank it, let that reach the DOM on its own,
    // then write the text back.
    if (useAnnouncementStore.getState()[politeness] === entry.message) {
      useAnnouncementStore.setState({ [politeness]: '' });
      this.timers[politeness] = setTimeout(
        () => this.write(politeness, entry),
        ANNOUNCEMENT_REPEAT_GAP_MS
      );

      return;
    }

    this.write(politeness, entry);
  }

  /** The one place text reaches a region, so the event is reported once. */
  private write(
    politeness: AnnouncementPoliteness,
    entry: PendingAnnouncement
  ): void {
    delete this.timers[politeness];

    useAnnouncementStore.setState({ [politeness]: entry.message });

    if (entry.event) {
      pushToGTMDataLayer(entry.event, { politeness });
    }
  }
}
